import React from 'react';

/**
 * UI refresh tokens (Nov 2026), matching the design canvas "Archy UI Refresh".
 * Green is the live app's #12B158 (Aaron: keep it vibrant). Status is shown by colour and icon only.
 */
export const ui = {
  ink: '#16181D',
  text2: '#2E333B',
  muted: '#5B616E',
  page: '#F5F6F8',
  surface: '#FFFFFF',
  subtle: '#F1F2F4',
  field: '#F9FAFB',
  line: '#E4E6EA',
  lineSoft: '#EEF0F2',
  control: '#DADDE2',
  accent: '#E5056A',
  link: '#B8045A',
  // The customer's colours (Admin › Settings › Colours; hooks/useThemeColours sets the variables).
  // The fallbacks are the defaults. Use these only in style values, where CSS variables work.
  complete: 'var(--archy-complete, #12B158)',
  incomplete: 'var(--archy-incomplete, #E5056A)',
  bucket: 'var(--archy-bucket, #9E1269)',
  /** The bucket colour, a shade darker (column heading rows under a bucket bar). */
  bucketDark: 'linear-gradient(rgba(0,0,0,0.2), rgba(0,0,0,0.2)), var(--archy-bucket, #9E1269)',
  radius: 14,
  font: "Inter, system-ui, -apple-system, 'Segoe UI', sans-serif",
};

/**
 * The frame every bucket on a project sits in (Videos & Audio, Assets, Notes, Gallery), the same
 * rounded shape as the project details box above them (Aaron, 5 Nov 2026).
 */
export const BUCKET_RADIUS = 16;
export const bucketFrame = {
  border: '1px solid #E4E6EA',
  borderRadius: `${BUCKET_RADIUS}px`,
  overflow: 'hidden',
  boxShadow: 'none',
  background: '#FFFFFF',
};

export const statusColor =(complete: boolean) => (complete ? ui.complete : ui.incomplete);

/** A small square icon button on a coloured status strip. */
export const stripButton: React.CSSProperties = {
  width: 36,
  height: 36,
  border: 'none',
  borderRadius: 8,
  background: 'rgba(0,0,0,0.18)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  flex: 'none',
};

export const button = (kind: 'primary' | 'plain' | 'quiet' = 'plain'): React.CSSProperties => ({
  height: 44,
  padding: '0 16px',
  borderRadius: 10,
  border: kind === 'plain' ? `1px solid ${ui.control}` : '1px solid transparent',
  background: kind === 'primary' ? ui.accent : kind === 'plain' ? ui.surface : 'transparent',
  color: kind === 'primary' ? '#FFFFFF' : kind === 'quiet' ? ui.muted : ui.ink,
  font: 'inherit',
  fontSize: 14,
  fontWeight: kind === 'primary' ? 600 : 500,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
});

/**
 * Storage labels (Aaron, 5 Nov 2026, second revision): short names. Preview is green (hot storage,
 * playable); the cold-storage labels are blue. Stored values stay preview / master / CAD / distribution.
 */
export const CATEGORY_LABEL: Record<string, string> = {
  preview: 'Preview',
  master: 'Master',
  CAD: 'Approval',
  distribution: 'Distribution',
};
export const CATEGORY_ORDER = ['preview', 'master', 'CAD', 'distribution'];

const GREEN_PILL = { fg: '#0B6B3F', bg: '#E3F6EC', border: '#A9DDBF' };
const BLUE_PILL = { fg: '#1E4E8C', bg: '#DCEBFA', border: '#B3CFEF' };
export const CATEGORY_PILL: Record<string, { fg: string; bg: string; border: string }> = {
  preview: GREEN_PILL,
  master: BLUE_PILL,
  CAD: BLUE_PILL,
  distribution: BLUE_PILL,
};

/**
 * One column layout for the Videos & Audio heading row and every file row, so they line up. The heading
 * row sits inside the same scrolling box as the rows, so a scrollbar narrows both alike (Aaron, 5 Nov 2026).
 * Last column: four buttons (information, download, move, delete).
 */
export const VIDEO_COLUMNS = '32px minmax(140px, 1fr) minmax(120px, 220px) 64px 72px 128px 72px 146px';

/** The file type (MOV, MP4, WAV…) as a small dark pill (Aaron, 5 Nov 2026). Its text starts TYPE_PILL_INSET in. */
export const TYPE_PILL_INSET = 8;
export const typePill: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 22,
  padding: `0 ${TYPE_PILL_INSET}px`,
  borderRadius: 999,
  background: '#2E333B',
  color: '#FFFFFF',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.04em',
  lineHeight: 1,
  whiteSpace: 'nowrap',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  boxSizing: 'border-box',
};
/** Where the label pill's text starts (its left padding plus border), so the heading can line up with it. */
export const LABEL_PILL_INSET = 13;
export const videoGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: VIDEO_COLUMNS,
  columnGap: 12,
  alignItems: 'center',
  padding: '0 16px',
  boxSizing: 'border-box',
  width: '100%',
};
